import { Link } from 'react-router-dom';

interface Props {
  banner: {
    title: string,
    subtitle?: string,
    imageUrl: string,
    links: {
      text: string;
      to: string
    }
  }
  bannerClassName?: string
  icons?: {
    icon: string,
    title: string,
    detail: string,
    height?: number
  }[]
  section: {
    title: string,
    description?: string
  }
  specialists: {
    id: string,
    name: string,
    imageUrl: string,
    countryImageUrl?: string,
    slug: string
    type: string
  }[]
  articles?: {
    href: string,
    img: string,
    date?: string,
    sourceImage?: string,
    headline: string,
    detail: string
  }[]
  article?: {
    title: string;
    subTitle: string;
  }
  learn?: {
    title: string,
    subtitle?: string,
    topics: {
      icon: string,
      title: string
    }[]
  }
  psychologyLearn?: {
    title: string,
    subtitle: string,
    topics: {
      icon: string,
      title: string
    }[]
  }
  howToSchedule?: {
    title: string,
    steps: {
      number: number,
      title: string,
      description: string
    }[]
  }
  learnMore?: {
    title: string,
    subtitle: string,
    articles: {
      href: string,
      imageUrl: string,
      title: string,
      description: string
    }[],
    ctaText: string,
    ctaLink: string
  }
}

export default function SpecialistPage(
  {
    banner,
    bannerClassName,
    icons,
    section,
    specialists,
    learn,
    psychologyLearn,
    howToSchedule,
  }: Props
) {
  return (
    <div className="w-full pb-35 bg-gray-100">
      <div
        className={`w-full bg-cover bg-center text-white relative ${bannerClassName || ''}`}
        style={{ backgroundImage: `url('${banner.imageUrl}')` }}
      >
        <div className="absolute inset-0 bg-black/30 z-0" />

        <div className="relative z-10 items-center h-full max-w-2xl py-25 px-5 text-left">
          <h1 className="text-6xl font-bold mb-4 text-white font-juana">{banner.title}</h1>
          {banner.subtitle && <p className="text-xl mb-6 text-white">{banner.subtitle}</p>}
          <div className="flex space-x-4">
            <Link
              to={banner.links.to}
              className="bg-holos-dark text-white px-6 py-3 rounded hover:bg-holos-dark/80 cursor-pointer"
            >
              {banner.links.text}
            </Link>
          </div>
        </div>
      </div>

      {icons && <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
        {icons.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <img
              src={`https://cache.beholos.com/img/fitness-pass-v2/${item.icon}`}
              alt={item.title}
              className="mb-4"
              style={{ height: item.height || 100 }}
            />
            <h4 className="text-lg font-semibold mb-2">{item.title}</h4>
            <p className="text-sm text-gray-700">{item.detail}</p>
          </div>
        ))}
      </div>}

      {psychologyLearn && (
        <section className="py-15">
          <div className="container mx-auto">
            <div className="text-left mb-5">
              <div className="text-4xl font-juana italic mb-4">
                {psychologyLearn.title}
              </div>
              <div className="text-lg">
                <strong>Encuentra especialistas de calidad sin salir de casa.</strong> {psychologyLearn.subtitle}
              </div>
            </div>

            <div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                {psychologyLearn.topics.slice(0, 4).map((topic, idx) => (
                  <div key={idx} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/psychology/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                {psychologyLearn.topics.slice(4, 8).map((topic, idx) => (
                  <div key={idx + 4} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/psychology/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {psychologyLearn.topics.slice(8, 12).map((topic, idx) => (
                  <div key={idx + 8} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/psychology/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {learn && (
        <section className="bg-gray-100 pt-15 pb-0">
          <div className="container mx-auto text-center md:text-left text-sm">
            <div className="mx-auto md:mx-0" style={{ maxWidth: '500px' }}>
              <div className="text-3xl font-bold italic mb-4">
                {learn.title}
              </div>
              {learn.subtitle && (
                <div className="text-sm text-gray-700">
                  {learn.subtitle}
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                {learn.topics.slice(0, 3).map((topic, idx) => (
                  <div key={idx} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/finance/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                {learn.topics.slice(3, 6).map((topic, idx) => (
                  <div key={idx + 3} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/finance/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {learn.topics.slice(6, 9).map((topic, idx) => (
                  <div key={idx + 6} className="bg-white flex items-center py-3 px-3 mx-3 md:mx-0" style={{ border: '1px solid #E1E3E6' }}>
                    <img
                      src={`https://cache.beholos.com/img/fitness-pass-v2/life/finance/${topic.icon}`}
                      style={{ width: '40px', height: '40px' }}
                      alt={topic.title}
                    />
                    <div className="flex-grow flex justify-center">
                      <div className="text-md font-medium text-left" style={{ lineHeight: '1em', color: '#A9A9AA' }}>
                        {topic.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="bg-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl text-center mb-4 font-juana">{section.title}</h2>
          {section.description && (
            <p className="text-lg text-gray-700 mb-8">{section.description}</p>
          )}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {specialists.map((specialist) => (
              <div key={specialist.id} className="bg-white shadow rounded overflow-hidden">
                <div
                  className="h-48 bg-center bg-cover"
                  style={{ backgroundImage: `url('${specialist.imageUrl}')` }}
                />
                <div className="p-4 text-center">
                  <div className="flex items-center justify-center mb-2 space-x-2">
                    <span className="font-medium">{specialist.name}</span>
                    {specialist.countryImageUrl && (
                      <img
                        src={specialist.countryImageUrl}
                        alt="country"
                        className="h-5"
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{specialist.type}</p>
                  <Link
                    to={`/partners/${specialist.slug}`}
                    className="inline-block w-full text-black px-4 py-2 rounded border font-medium mb-2 cursor-pointer"
                  >
                    Agendar Sesión
                  </Link>
                  <Link
                    to={`/partners/${specialist.slug}`}
                    className="inline-block text-holos-dark text-xs cursor-pointer"
                  >
                    Ver perfil &rarr;
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {howToSchedule && (
        <section className="mx-auto pt-15">
          <div className="container mx-auto mb-5 md:mb-0">
            <h2 className="text-center mb-5 text-3xl font-bold">{howToSchedule.title}</h2>
            <div className="my-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-5">
                {howToSchedule.steps.map((step, idx) => (
                  <div key={idx} className="relative font-bold mt-5 mb-3">
                    <div
                      className="text-center"
                      style={{
                        border: '1px solid #E1E3E6',
                        borderRadius: '0px 0px 4px 4px',
                        height: '200px',
                        paddingTop: '80px'
                      }}
                    >
                      <div
                        className="text-2xl text-white bg-black font-bold absolute"
                        style={{
                          borderRadius: '50%',
                          width: '100px',
                          height: '100px',
                          top: '-50px',
                          left: '50%',
                          marginLeft: '-50px',
                          lineHeight: '100px'
                        }}
                      >
                        {step.number}
                      </div>
                      <div className="text-lg text-black font-bold mb-2">{step.title}</div>
                      <div className="text-base text-gray-600 font-normal mt-2 px-4">{step.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
